export type CardinalPlacement = 'top' | 'right' | 'bottom' | 'left';

const TOOLTIP_RADIAL_PADDING = 12;
const TWO_PI = 2 * Math.PI;

/**
 * Anchor a tooltip just outside the labels at the given angle, so the tooltip
 * and its arrow clear both the ring and any curved/flipped label text sitting
 * around it.
 *
 * d3-chord angles: 0 = 12 o'clock, increasing clockwise.
 */
export const getRadialTooltipAnchor = ({
  angle,
  outerRadius,
  labelOffset,
  labelFontSize,
}: {
  angle: number;
  outerRadius: number;
  labelOffset: number;
  labelFontSize: number;
}): { x: number; y: number } => {
  const radius =
    outerRadius + labelOffset + labelFontSize + TOOLTIP_RADIAL_PADDING;
  return {
    x: radius * Math.sin(angle),
    y: -radius * Math.cos(angle),
  };
};

/**
 * Pick a cardinal placement so the tooltip extends radially outward from the
 * chart center instead of folding back over the ring.
 *
 * Sectors are π/2 wide and centered on each cardinal direction:
 *   top    around angle 0       (12 o'clock)
 *   right  around angle  π/2    (3  o'clock)
 *   bottom around angle  π      (6  o'clock)
 *   left   around angle  3π/2   (9  o'clock)
 */
export const getRadialTooltipPlacement = (angle: number): CardinalPlacement => {
  const a = ((angle % TWO_PI) + TWO_PI) % TWO_PI;
  if (a < Math.PI / 4 || a >= (7 * Math.PI) / 4) return 'top';
  if (a < (3 * Math.PI) / 4) return 'right';
  if (a < (5 * Math.PI) / 4) return 'bottom';
  return 'left';
};
